import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload } from "lucide-react";

function DocLink({ path, filename, label, download, testid }) {
  const go = async () => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); }
      else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the document"); }
  };
  return <button type="button" onClick={go} className="text-terracotta underline" data-testid={testid}>{label}</button>;
}

const TYPES = [["PACKING_LIST", "Packing list"], ["BILL_OF_LADING", "Bill of Lading"], ["SUPPLIER_INVOICE", "Supplier invoice"], ["FREIGHT", "Freight document"], ["OTHER", "Other"]];

function UploadForm({ cid, suppliers, replaces, onDone }) {
  const [type, setType] = useState(replaces?.doc_type || "PACKING_LIST");
  const [sup, setSup] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const fd = new FormData();
    fd.append("doc_type", type); fd.append("supplier_id", sup); fd.append("replaces_id", replaces?.id || ""); fd.append("file", file);
    setBusy(true);
    try { await api.post(`/admin/containers/${cid}/documents`, fd); toast.success(replaces ? "New version uploaded" : "Document uploaded"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const tid = replaces ? `doc-newversion-${replaces.id}` : "doc-upload";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${tid}-form`}>
      {!replaces && (
        <>
          <Select value={type} onValueChange={setType}><SelectTrigger className="h-9 w-44 bg-white" data-testid="doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Select value={sup || "none"} onValueChange={(v) => setSup(v === "none" ? "" : v)}><SelectTrigger className="h-9 w-44 bg-white" data-testid="doc-supplier"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">No specific supplier</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>)}</SelectContent></Select>
        </>
      )}
      <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9 w-64 bg-white text-xs" data-testid={`${tid}-file`} />
      <Button size="sm" disabled={!file || busy} onClick={send} className="h-9 gap-1 bg-charcoal text-white hover:bg-charcoal/85" data-testid={`${tid}-submit`}><Upload className="h-3 w-3" /> Upload</Button>
    </div>
  );
}

// ID-054: permanent, versioned container documents (admin-only)
export function ContainerDocuments({ cid, docs, suppliers, onChanged }) {
  const [replacing, setReplacing] = useState(null);
  const supName = (id) => suppliers.find((s) => s.id === id)?.code || "";
  return (
    <div data-testid="container-documents">
      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Documents</p>
      <div className="mt-2 space-y-1">
        {docs.length === 0 && <p className="text-xs text-muted-foreground" data-testid="docs-empty">No documents yet — upload them in any order.</p>}
        {docs.map((d) => (
          <div key={d.id} className={`flex flex-wrap items-center gap-2 text-xs ${d.is_current ? "text-charcoal" : "text-muted-foreground"}`} data-testid={`doc-row-${d.id}`}>
            <span className="font-medium">{d.type_label}</span>{d.supplier_id && <span>· {supName(d.supplier_id)}</span>}
            <span>· v{d.version}</span><span className={`badge-status ${d.is_current ? "badge-available" : "badge-neutral"}`}>{d.is_current ? "current" : "superseded"}</span>
            <span className="truncate">{d.filename}</span><span>· {new Date(d.uploaded_at).toLocaleString()}</span>
            <DocLink path={`/admin/container-documents/${d.id}/file`} filename={d.filename} label="View" testid={`doc-view-${d.id}`} />
            <DocLink path={`/admin/container-documents/${d.id}/file?download=true`} filename={d.filename} label="Download" download testid={`doc-download-${d.id}`} />
            {d.is_current && <button type="button" className="text-terracotta underline" onClick={() => setReplacing(replacing?.id === d.id ? null : d)} data-testid={`doc-replace-${d.id}`}>Upload new version</button>}
            {replacing?.id === d.id && <UploadForm cid={cid} suppliers={suppliers} replaces={d} onDone={() => { setReplacing(null); onChanged(); }} />}
          </div>
        ))}
      </div>
      <div className="mt-3"><UploadForm cid={cid} suppliers={suppliers} onDone={onChanged} /></div>
    </div>
  );
}

export function useSuppliers() {
  const [s, setS] = useState([]);
  useEffect(() => { api.get("/admin/suppliers").then(({ data }) => setS(data)).catch(() => {}); }, []);
  return s;
}
